/**
 * Every place an admin can go, in one list.
 *
 * Read by the jump-to box (Ctrl/Cmd+K) and the "g then …" shortcuts. The
 * Admin Hub keeps its own cards; adminDestinations.test.js fails if a Hub
 * card links somewhere this list doesn't, so the two can't drift.
 *
 * The main console's sections live in the address too (/admin?section=…), so
 * a section can be refreshed, bookmarked and reached with the back button.
 */

/** The main console's sections, in sidebar order. 'overview' is the default. */
export const ADMIN_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'webinars', label: 'Webinars' },
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'customers', label: 'Customers' },
  { id: 'billing-health', label: 'Billing health', keywords: 'payments failed past due stripe' },
  { id: 'abandoned-checkouts', label: 'Abandoned checkouts', keywords: 'checkout cart' },
  { id: 'growth', label: 'Growth', keywords: 'signups' },
  { id: 'activation', label: 'Activation', keywords: 'onboarding' },
  { id: 'settings', label: 'Settings', keywords: 'site config' },
  { id: 'infrastructure', label: 'Infrastructure', keywords: 'costs servers' },
  { id: 'university', label: 'University', keywords: 'videos lessons' },
  { id: 'sales-leads', label: 'Sales leads', keywords: 'demo requests founding applications' },
  { id: 'public-feed', label: 'Public feed', keywords: 'explore moderation roku featured' },
  { id: 'affiliates', label: 'Affiliates (overview)', keywords: 'referrals' },
];

const SECTION_IDS = new Set(ADMIN_SECTIONS.map((s) => s.id));

/** A section id from the address, or 'overview' for anything unknown. */
export function normalizeAdminSection(value) {
  return SECTION_IDS.has(value) ? value : 'overview';
}

/** The address of a console section (overview has no ?section=). */
export function adminSectionPath(id, extra = {}) {
  const params = new URLSearchParams();
  if (id && id !== 'overview') params.set('section', id);
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });
  const qs = params.toString();
  return qs ? `/admin?${qs}` : '/admin';
}

/** The admin pages outside the main console. */
export const ADMIN_PAGES = [
  { label: 'Admin Hub', path: '/admin-hub', keywords: 'home all tools' },
  { label: 'Tracking & acquisition', path: '/admin/tracking', keywords: 'pixel ga4 utm' },
  { label: 'Developer', path: '/admin/developer', keywords: 'health logs errors' },
  { label: 'Monitoring', path: '/admin/monitoring', keywords: 'alerts security audit' },
  { label: 'Compliance', path: '/admin/compliance', keywords: 'gdpr dsar consent retention' },
  { label: 'Affiliate admin', path: '/admin/affiliates', keywords: 'referrals payouts commissions' },
  { label: 'Coupons', path: '/admin/coupons', keywords: 'discount promo' },
  { label: 'Builder success (support)', path: '/admin/support', keywords: 'tickets help' },
  { label: 'Feature flags', path: '/admin/features', keywords: 'toggles' },
  { label: 'Analytics', path: '/admin/analytics' },
  { label: 'AI usage', path: '/admin/ai-usage', keywords: 'spend quota tokens' },
  { label: 'Cost per customer', path: '/admin/customer-costs', keywords: 'margin profit unit economics' },
  { label: 'Demo booking', path: '/admin/booking-settings', keywords: 'calendar meetings' },
  { label: 'CANIRA TV sponsors', path: '/admin/sponsors' },
  { label: 'CANIRA TV videos', path: '/admin/canira-tv-videos' },
  { label: 'CANIRA TV hosts', path: '/admin/canira-tv-hosts' },
  { label: 'CANIRA TV ticker', path: '/admin/ticker-items' },
  { label: 'White-label Roku channels', path: '/admin/roku-channels', keywords: 'tv' },
  { label: 'Deploy safety & maintenance', path: '/admin/deploy', keywords: 'release banner' },
  { label: 'Storage cleanup', path: '/admin/storage-cleanup', keywords: 'retention stream minutes videos' },
  { label: 'Phone app numbers', path: '/admin/app-stats', keywords: 'mobile install qr scans' },
  { label: 'Homepage training card', path: '/admin/home-training' },
  { label: 'Canira Weekly email', path: '/admin/weekly-email', keywords: 'newsletter tips marketing' },
  { label: 'Announce to Canira accounts', path: '/admin/platform-announce', keywords: 'email blast' },
  { label: 'Send test SMS', path: '/admin/sms-test', keywords: 'text twilio' },
  { label: 'Billing status', path: '/admin/billing', keywords: 'stripe resync drift' },
  { label: 'Social OAuth status', path: '/admin/social-status', keywords: 'facebook youtube linkedin' },
  { label: 'Test autonomous host', path: '/admin/autonomous-host-test' },
];

/** Everything the jump-to box offers, sections first. */
export const ADMIN_DESTINATIONS = [
  ...ADMIN_SECTIONS.map((s) => ({
    key: `section:${s.id}`,
    label: s.label,
    hint: 'Console',
    keywords: s.keywords || '',
    path: adminSectionPath(s.id),
  })),
  ...ADMIN_PAGES.map((p) => ({
    key: `page:${p.path}`,
    label: p.label,
    hint: 'Page',
    keywords: p.keywords || '',
    path: p.path,
  })),
];

/**
 * Destinations matching what was typed: every word must appear in the label
 * or keywords. Label-prefix matches come first.
 */
export function filterDestinations(query, list = ADMIN_DESTINATIONS) {
  const words = String(query || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return list;
  const scored = [];
  list.forEach((d, i) => {
    const label = d.label.toLowerCase();
    const hay = `${label} ${d.keywords}`.toLowerCase();
    if (!words.every((w) => hay.includes(w))) return;
    const score = label.startsWith(words[0]) ? 0 : label.includes(words[0]) ? 1 : 2;
    scored.push({ d, score, i });
  });
  scored.sort((a, b) => a.score - b.score || a.i - b.i);
  return scored.map((s) => s.d);
}

/** "g then <key>" shortcuts. */
export const GO_SHORTCUTS = [
  { key: 'h', label: 'Admin Hub', path: '/admin-hub' },
  { key: 'o', label: 'Overview', path: adminSectionPath('overview') },
  { key: 'u', label: 'Users', path: adminSectionPath('users') },
  { key: 'b', label: 'Billing health', path: adminSectionPath('billing-health') },
  { key: 'l', label: 'Sales leads', path: adminSectionPath('sales-leads') },
  { key: 's', label: 'Storage cleanup', path: '/admin/storage-cleanup' },
  { key: 'm', label: 'Monitoring', path: '/admin/monitoring' },
];

/** Where the shortcuts and jump-to box are switched on. */
export function isAdminPath(pathname) {
  const p = String(pathname || '');
  return p === '/admin' || p.startsWith('/admin/') || p === '/admin-hub';
}

/** What the jump-to box can do to a user it found. Each opens the console. */
export const USER_ACTIONS = [
  { id: 'view', label: 'Open' },
  { id: 'extend-trial', label: 'Extend trial' },
  { id: 'change-plan', label: 'Change plan' },
];
const USER_ACTION_IDS = new Set(USER_ACTIONS.map((a) => a.id));

/** The address that opens a user (and one of the actions above) in the console. */
export function adminUserPath(userId, action = 'view') {
  return adminSectionPath('users', {
    user: userId,
    action: USER_ACTION_IDS.has(action) && action !== 'view' ? action : undefined,
  });
}

/** The action named in the address, or 'view'. */
export function normalizeUserAction(value) {
  return USER_ACTION_IDS.has(value) ? value : 'view';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A user id from the address, or null (never sent to the server unchecked). */
export function normalizeUserId(value) {
  return typeof value === 'string' && UUID_RE.test(value) ? value : null;
}
